import { API } from 'aws-amplify';
import { AxiosError } from 'axios';

import { createSlug, replaceSpaces } from '../infrastructure/utils/common';
import {
  Activity,
  Availabilities,
  Category,
  GetAvailabilitiesPayload,
  SuggestedActivity,
} from '../types/Activity';

export type AdminSearchDefaultQuery = { match_all: Record<string, unknown> };
export type AdminSearchFilterQuery = {
  bool: { must: unknown[]; must_not: unknown[] };
};
export type SortCondition = {
  order: 'ASC' | 'DESC';
  missing?: '_first' | '_last';
};
export type AdminSearchSort = Record<
  string,
  'ASC' | 'DESC' | SortCondition | unknown
>[];

export const adminSearch = async (
  query: AdminSearchDefaultQuery | AdminSearchFilterQuery,
  sort: AdminSearchSort,
  from: number = 0,
  size: number = 25,
  projection: string[] = []
): Promise<Activity[]> => {
  try {
    const response = await API.post('party', '/activities/adminSearch', {
      body: {
        from,
        size,
        sort,
        query,
        projection,
      },
    });

    if (typeof response === 'string') {
      const responseValue = await fetch(<string>response);
      return responseValue.json();
    } else {
      return response;
    }
  } catch (e) {
    console.error(e);
    const error = e as AxiosError;
    alert(
      `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
    );
    return [];
  }
};

export function getActivityByCity(
  city: string,
  featured?: boolean
): Promise<Activity[]> {
  let param = '?withHidden=true';
  if (!featured) {
    param = '&featured=false';
  }
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', `/activities/city/${city}${param}`);
}

export function getById(id): Promise<Activity> {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', `/activities/${encodeURIComponent(id)}`);
}

export function getSuggestedActivity(id: string): Promise<SuggestedActivity> {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', `/activities/${id}/suggestion`);
}

export function getSourceById(id) {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', `/activities/${encodeURIComponent(id)}/source`);
}

export function createActivity(activity) {
  return API.post('party', '/activities', { body: { ...activity } });
}

export function deleteActivity(id) {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.del('party', `/activities/${id}`);
}
export function uploadPhoto(activityId, pictureId, data) {
  API.put('party', `/activities/${activityId}/pictures/${pictureId}`, {
    body: data,
  });
}

export function getAllTags() {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', '/activities/tags');
}

export function getActivityByTag(
  city: string,
  tag?: string,
  price?: string,
  bookable?: boolean
) {
  let tags = tag;
  if (tags === undefined) {
    tags = '';
  }
  if (bookable) {
    tags = `${tags}, bookable`;
  }
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get(
    'party',
    `/activities/search?city=${city}${tags ? `&tags=${tags}` : ''}${
      price ? `&price=${price}` : ''
    }`
  );
}

export function changeTagType(tag) {
  return API.put('party', '/activities/tags', { body: { ...tag } });
}

export function getCities() {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', '/activities/cities');
}

export function changeCityStatus(data) {
  return API.put('party', '/activities/cities', {
    body: {
      ...data,
    },
  });
}

export function updateCityField(data, city: string) {
  const slug = createSlug(city);
  return API.patch('party', `/cities/${slug}`, {
    body: data,
  });
}

export async function getCity(city: string) {
  const slug = createSlug(city);
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', `/cities/${slug}`);
}

export async function getCityBySlug(slug: string) {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', `/cities/${slug}`);
}

export function deleteCity(name) {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.del('party', `/activities/cities/${encodeURIComponent(name)}`);
}

export function getUrlForCitiesImage(name, type) {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get(
    'party',
    `/activities/get-signed-url/${type || 'cities'}/${name}`
  );
}

export async function searchByName(name) {
  try {
    const response = await API.post('party', '/activities/adminSearch', {
      body: {
        from: 0,
        size: 20,
        query: {
          match_phrase_prefix: {
            title: name,
          },
        },
      },
    });
    if (typeof response === 'string') {
      const responseValue = await fetch(<string>response);
      return responseValue.json();
    } else {
      return response;
    }
  } catch (e) {
    console.error(e);
    const error = e as AxiosError;
    alert(
      `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
    );
  }
}

export async function searchByVendor(id) {
  try {
    const response = await API.post('party', '/activities/adminSearch', {
      body: {
        from: 0,
        size: 1000,
        query: {
          match_phrase_prefix: {
            vendorId: id,
          },
        },
      },
    });
    if (typeof response === 'string') {
      const responseValue = await fetch(<string>response);
      return responseValue.json();
    } else {
      return response;
    }
  } catch (e) {
    console.error(e);
    const error = e as AxiosError;
    alert(
      `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
    );
  }
}

export function getRnRsByActivityId(id) {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', `/reviews/${id}`);
}

export function updateActivity(id, activity) {
  return API.patch('party', `/activities/${id}`, {
    body: activity,
  });
}

export function createPreSignedImgUrl(id, image) {
  const fileName = replaceSpaces(image);
  return API.post('party', `/activities/${id}/image`, {
    body: { filename: fileName },
  });
}

export function createBookingOption(id, option) {
  return API.post('party', `/activities/${id}/booking-options`, {
    body: option,
  });
}

export function updateBookingOption(activityId, optionId, changedFields) {
  return API.patch(
    'party',
    `/activities/${activityId}/booking-options/${optionId}`,
    {
      body: changedFields,
    }
  );
}

export function deleteBookingOption(activityId, optionId) {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.del(
    'party',
    `/activities/${activityId}/booking-options/${optionId}`
  );
}

export function getActivityAvgRating(id) {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', `/reviews/${id}/stats`);
}

export function uploadBulkActivities(activities) {
  return API.post('party', '/activities/bulk', {
    body: activities,
  });
}

export async function getFHItems() {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', '/fareharbor/items');
}

export async function getActivityFromFareharborItem(pk) {
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get('party', `/fareharbor/items/${pk}/build-activity`);
}

export function syncFHItems() {
  return API.put('party', `/fareharbor/sync`, {});
}

export function getBookingOptionAvailabilities(
  payload: GetAvailabilitiesPayload
): Promise<Availabilities[]> {
  const { activityId, bookingOptionId, startDate, endDate, tz } = payload;
  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
  // @ts-ignore
  return API.get(
    'party',
    `/activities/${activityId}/availabilities?bookingOptionId=${bookingOptionId}&startDate=${startDate}&endDate=${endDate}&tz=${tz}`
  );
}

export const getSignedUploadUrlForActivity = (
  activityId: string,
  type: string,
  contentType: string
): Promise<{ id: string; url: string; uploadUrl: string }> => {
  return API.get('party', `/activities/${activityId}/get-signed-url/${type}`, {
    queryStringParameters: {
      contentType,
    },
  });
};

export const listCategories = (): Promise<Category[]> => {
  return API.get('party', '/activities/categories', {});
};
